/**
 * ClassTemplate type definitions matching backend schemas exactly.
 * These types ensure type safety and consistency with the API.
 */
import { z } from "zod"

// Base classTemplate properties matching ClassTemplateBase schema
export interface ClassTemplateBase {
  name: string
  slug: string
  description: string | null
  program_type: string
  instructor_id: string | null
  swim_level: string
  location: string
  day_of_week:
    | "Monday"
    | "Tuesday"
    | "Wednesday"
    | "Thursday"
    | "Friday"
    | "Saturday"
    | "Sunday"
  start_time: string
  end_time: string | null
  duration_minutes: number | null
  max_students: number
  min_students: number | null
  current_enrollment: number | null
  waitlist_enabled: boolean | null
  price: number
  prerequisites: string | null
  notes: string | null
  is_active: boolean | null
  is_published: boolean | null
}

// Create classTemplate data matching ClassTemplateCreate schema
export interface ClassTemplateCreate extends ClassTemplateBase {}

// Update classTemplate data matching ClassTemplateUpdate schema
export interface ClassTemplateUpdate {
  name?: string
  slug?: string
  description?: string | null
  program_type?: string
  instructor_id?: string | null
  swim_level?: string
  location?: string
  day_of_week?:
    | "Monday"
    | "Tuesday"
    | "Wednesday"
    | "Thursday"
    | "Friday"
    | "Saturday"
    | "Sunday"
  start_time?: string
  end_time?: string | null
  duration_minutes?: number | null
  max_students?: number
  min_students?: number | null
  current_enrollment?: number | null
  waitlist_enabled?: boolean | null
  price?: number
  prerequisites?: string | null
  notes?: string | null
  is_active?: boolean | null
  is_published?: boolean | null
}

// Full classTemplate response matching ClassTemplateResponse schema
export interface ClassTemplatePublic extends ClassTemplateBase {
  id: string
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

// Response wrapper for API calls
export interface ClassTemplateResponse extends ClassTemplatePublic {
  // Enriched field populated by the backend
  instructor_name?: string | null
}

// Paginated list response matching ClassTemplateListResponse schema
export interface ClassTemplateListResponse {
  items: ClassTemplateResponse[]
  total: number
  page: number
  per_page: number
  pages: number
  meta: ClassTemplateListMeta
}

export interface ClassTemplateListMeta {
  sort_by: "created_at" | "updated_at"
  sort_order: "asc" | "desc"
  filters: ClassTemplateFilters
}

// Form data interfaces for UI components
export interface ClassTemplateFormData {
  name?: string
  slug?: string
  description?: string | null
  program_type?: string
  instructor_id?: string | null
  swim_level?: string
  location?: string
  day_of_week?:
    | "Monday"
    | "Tuesday"
    | "Wednesday"
    | "Thursday"
    | "Friday"
    | "Saturday"
    | "Sunday"
  start_time?: string
  end_time?: string | null
  duration_minutes?: number | null
  max_students?: number
  min_students?: number | null
  current_enrollment?: number | null
  waitlist_enabled?: boolean | null
  price?: number
  prerequisites?: string | null
  notes?: string | null
  is_active?: boolean | null
  is_published?: boolean | null
}

export interface ClassTemplateUpdateFormData {
  name?: string
  slug?: string
  description?: string | null
  program_type?: string
  instructor_id?: string | null
  swim_level?: string
  location?: string
  day_of_week?:
    | "Monday"
    | "Tuesday"
    | "Wednesday"
    | "Thursday"
    | "Friday"
    | "Saturday"
    | "Sunday"
  start_time?: string
  end_time?: string | null
  duration_minutes?: number | null
  max_students?: number
  min_students?: number | null
  current_enrollment?: number | null
  waitlist_enabled?: boolean | null
  price?: number
  prerequisites?: string | null
  notes?: string | null
  is_active?: boolean | null
  is_published?: boolean | null
}

// Filter interface for API queries
export interface ClassTemplateFilters {
  limit?: number
  skip?: number
  search?: string
  sort_by?: "created_at" | "updated_at"
  sort_order?: "asc" | "desc"
  created_by?: string
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
}

// Component prop interfaces following sophisticated pattern
export interface AddClassTemplateProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export interface EditClassTemplateProps {
  class_template: ClassTemplateResponse
  onSuccess?: () => void
  onCancel?: () => void
}

export interface DeleteClassTemplateProps {
  class_template: ClassTemplateResponse
  onSuccess?: () => void
  onCancel?: () => void
}

// Zod validation schemas for forms
export const class_templateCreateSchema = z.object({
  name: z.string().trim(),
  slug: z.string().trim(),
  description: z.string().trim().nullable().optional(),
  program_type: z.string().trim(),
  instructor_id: z.string().trim().max(32).nullable().optional(),
  swim_level: z.string().trim(),
  location: z.string().trim(),
  day_of_week: z.enum([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ]),
  start_time: z.string().trim(),
  end_time: z.string().trim().nullable().optional(),
  duration_minutes: z.coerce.number().int().nullable().optional(),
  max_students: z.coerce.number().int(),
  min_students: z.coerce.number().int().nullable().optional(),
  current_enrollment: z.coerce.number().int().nullable().optional(),
  waitlist_enabled: z.boolean().nullable().optional(),
  price: z.coerce.number(),
  prerequisites: z.string().trim().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  is_published: z.boolean().nullable().optional(),
})

export const class_templateUpdateSchema = z.object({
  name: z.string().trim().optional(),
  slug: z.string().trim().optional(),
  description: z.string().trim().nullable().optional(),
  program_type: z.string().trim().optional(),
  instructor_id: z.string().trim().max(32).nullable().optional(),
  swim_level: z.string().trim().optional(),
  location: z.string().trim().optional(),
  day_of_week: z
    .enum([
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ])
    .optional(),
  start_time: z.string().trim().optional(),
  end_time: z.string().trim().nullable().optional(),
  duration_minutes: z.coerce.number().int().nullable().optional(),
  max_students: z.coerce.number().int().optional(),
  min_students: z.coerce.number().int().nullable().optional(),
  current_enrollment: z.coerce.number().int().nullable().optional(),
  waitlist_enabled: z.boolean().nullable().optional(),
  price: z.coerce.number().optional(),
  prerequisites: z.string().trim().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  is_published: z.boolean().nullable().optional(),
})

// Type inference from Zod schemas
export type ClassTemplateCreateInput = z.infer<
  typeof class_templateCreateSchema
>
export type ClassTemplateUpdateInput = z.infer<
  typeof class_templateUpdateSchema
>
